<template>
    <div class="flex flex-col items-start gap-6 self-stretch w-full">
        <div class="relative self-stretch w-full h-[260px] sm:h-[300px]">
            <Bar
                v-if="hasData && !loading"
                :data="chartData"
                :options="chartOptions"
            />
            <div
                v-else
                class="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
                <ChartStatePlaceholder :loading="loading" />
            </div>
        </div>
        <div
            v-if="!loading"
            class="flex justify-center items-center gap-6 self-stretch"
        >
            <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-[#FD421D]" />
                <span
                    class="text-slate-800 dark:text-slate-100 font-inter text-sm font-normal leading-5"
                >
                    Used
                </span>
            </div>
            <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-[#E2E8F0]" />
                <span
                    class="text-slate-800 dark:text-slate-100 font-inter text-sm font-normal leading-5"
                >
                    Unused
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import {
    createExternalTooltip,
    formatTooltipMoney,
    isDarkMode,
    tooltipHeader,
    tooltipRow,
} from '@/components/card-benefits/chart-tooltip';
import ChartStatePlaceholder from './chart-state-placeholder.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const props = defineProps({
    series: {
        type: Array,
        required: true,
    },
});

const store = useCardBenefitsStore();
const { loading } = storeToRefs(store);

const hasData = computed(() =>
    props.series.some((s) => (s.used || 0) + (s.unused || 0) > 0)
);

// Y-axis top value: tallest bar + 5–10% headroom, rounded up to a clean
// tick. Using `grace: '10%'` lets Chart.js's auto-tick algorithm pick a
// nice round number (e.g. peak 140 → 160).
const chartData = computed(() => ({
    labels: props.series.map((s) => s.label),
    datasets: [
        {
            label: 'Used',
            data: props.series.map((s) => s.used),
            backgroundColor: '#FD421D',
            hoverBackgroundColor: '#FD7156',
            // When the unused segment is 0 the Used bar is the top of the
            // stack and should round its top corners as well.
            borderRadius: (ctx) => {
                const unused = props.series[ctx.dataIndex]?.unused || 0;
                return unused > 0
                    ? {
                          topLeft: 0,
                          topRight: 0,
                          bottomLeft: 4,
                          bottomRight: 4,
                      }
                    : {
                          topLeft: 4,
                          topRight: 4,
                          bottomLeft: 4,
                          bottomRight: 4,
                      };
            },
            borderSkipped: false,
            stack: 'usage',
            minBarThickness: 40,
            maxBarThickness: 120,
        },
        {
            label: 'Unused',
            data: props.series.map((s) => s.unused),
            backgroundColor: '#E2E8F0',
            hoverBackgroundColor: '#CBD5E1',
            // Mirror the Used logic: when used is 0 the Unused bar fills the
            // stack on its own and needs all four corners rounded.
            borderRadius: (ctx) => {
                const used = props.series[ctx.dataIndex]?.used || 0;
                return used > 0
                    ? {
                          topLeft: 4,
                          topRight: 4,
                          bottomLeft: 0,
                          bottomRight: 0,
                      }
                    : {
                          topLeft: 4,
                          topRight: 4,
                          bottomLeft: 4,
                          bottomRight: 4,
                      };
            },
            borderSkipped: false,
            stack: 'usage',
            minBarThickness: 40,
            maxBarThickness: 120,
        },
    ],
}));

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
            enabled: false,
            external: createExternalTooltip({
                containerStyle: 'min-width: 168px; width: 196px;',
                buildBody: (model) => {
                    const idx = model.dataPoints?.[0]?.dataIndex;
                    if (idx == null) return '';
                    const point = props.series[idx];
                    if (!point) return '';

                    const headerHtml = tooltipHeader(point.label);

                    const usedHtml = tooltipRow({
                        dot: '#FD421D',
                        label: 'Used',
                        value: formatTooltipMoney(point.used),
                    });

                    const breakdown = point.usedByPeriod || {};
                    const breakdownHtml = Object.entries(breakdown)
                        .filter(([, v]) => v > 0)
                        .sort(([, a], [, b]) => b - a)
                        .map(([label, value]) =>
                            tooltipRow({
                                label,
                                value: formatTooltipMoney(value),
                                indent: true,
                                dim: true,
                            })
                        )
                        .join('');

                    const unusedHtml = tooltipRow({
                        dot: '#E2E8F0',
                        label: 'Unused',
                        value: formatTooltipMoney(point.unused),
                    });

                    return `${headerHtml}${usedHtml}${breakdownHtml}${unusedHtml}`;
                },
            }),
        },
    },
    scales: {
        x: {
            stacked: true,
            grid: { display: false, drawBorder: false },
            ticks: {
                color: () => (isDarkMode() ? '#94A3B8' : '#64748B'),
                font: {
                    family: 'Inter',
                    size: 12,
                    weight: 400,
                    lineHeight: 16 / 12,
                },
            },
        },
        y: {
            stacked: true,
            beginAtZero: true,
            grace: '10%',
            border: { display: false },
            grid: { color: () => (isDarkMode() ? '#334155' : '#F1F5F9'), drawBorder: false },
            ticks: {
                color: () => (isDarkMode() ? '#94A3B8' : '#64748B'),
                font: {
                    family: 'Inter',
                    size: 12,
                    weight: 400,
                    lineHeight: 16 / 12,
                },
                maxTicksLimit: 3,
                callback: (value) => value,
            },
        },
    },
}));
</script>
